// O Markdown dos documentos legais, das páginas Sobre e Contato e de qualquer texto do site que precise sair igual em
// HTML e em Markdown. Lê só o que esses textos usam: títulos (#, ## e ###), parágrafos de uma linha, listas com "- ",
// tabelas com a linha de cabeçalho, **negrito** e [links](endereço). O jogo monta os blocos em React; o site, em HTML.

export type Trecho =
  | { t: 'texto'; texto: string }
  | { t: 'negrito'; texto: string }
  | { t: 'link'; texto: string; href: string };

export type Bloco =
  | { tipo: 'h1' | 'h2' | 'h3'; texto: string }
  | { tipo: 'p'; trechos: Trecho[] }
  | { tipo: 'ul'; itens: Trecho[][] }
  | { tipo: 'tabela'; cabecalho: string[]; linhas: string[][] };

const INLINE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

export function lerTrechos(linha: string): Trecho[] {
  const trechos: Trecho[] = [];
  let ultimo = 0;
  for (const m of linha.matchAll(INLINE)) {
    if (m.index > ultimo) trechos.push({ t: 'texto', texto: linha.slice(ultimo, m.index) });
    trechos.push(m[1] != null ? { t: 'negrito', texto: m[1] } : { t: 'link', texto: m[2]!, href: m[3]! });
    ultimo = m.index + m[0].length;
  }
  if (ultimo < linha.length) trechos.push({ t: 'texto', texto: linha.slice(ultimo) });
  return trechos;
}

const celulas = (linha: string) => linha.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

export function lerMarkdown(md: string): Bloco[] {
  const blocos: Bloco[] = [];
  const linhas = md.replace(/\r\n/g, '\n').split('\n');
  for (let i = 0; i < linhas.length; i++) {
    const linha = linhas[i]!.trimEnd();
    if (!linha.trim()) continue;
    const titulo = /^(#{1,3}) (.+)$/.exec(linha);
    if (titulo) { blocos.push({ tipo: `h${titulo[1]!.length}` as 'h1' | 'h2' | 'h3', texto: titulo[2]!.trim() }); continue; }
    if (linha.startsWith('- ')) {
      const itens: Trecho[][] = [];
      for (; i < linhas.length && linhas[i]!.startsWith('- '); i++) itens.push(lerTrechos(linhas[i]!.slice(2).trim()));
      i--;
      blocos.push({ tipo: 'ul', itens });
      continue;
    }
    if (linha.startsWith('|')) {
      const tabela: string[] = [];
      for (; i < linhas.length && linhas[i]!.startsWith('|'); i++) tabela.push(linhas[i]!);
      i--;
      if (tabela.length < 2 || !/^\|\s*:?-+/.test(tabela[1]!)) throw new Error(`tabela sem a linha de cabeçalho: ${tabela[0]}`);
      blocos.push({ tipo: 'tabela', cabecalho: celulas(tabela[0]!), linhas: tabela.slice(2).map(celulas) });
      continue;
    }
    blocos.push({ tipo: 'p', trechos: lerTrechos(linha.trim()) });
  }
  return blocos;
}

// O texto de um bloco, sem marcação: o título de uma seção ou um parágrafo.
export const textoDosTrechos = (trechos: Trecho[]): string => trechos.map((t) => t.texto).join('');

// O documento: título (# ...), data ("Atualizado em 18/10/2026.", quando houver) e o resto.
export function lerDocumento(md: string): { titulo: string; atualizado: string | null; blocos: Bloco[] } {
  const blocos = lerMarkdown(md);
  const h1 = blocos[0];
  if (h1?.tipo !== 'h1') throw new Error('documento sem o título (# ...) na primeira linha');
  const data = blocos[1]?.tipo === 'p' ? /^Atualizado em (.+)\.$/.exec(textoDosTrechos(blocos[1].trechos)) : null;
  return { titulo: h1.texto, atualizado: data?.[1] ?? null, blocos: blocos.slice(data ? 2 : 1) };
}

// O endereço de cada título, para o sumário: "15.3 Pacotes" vira "15-3-pacotes".
export function ancora(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const escapar = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function trechosEmHtml(trechos: Trecho[]): string {
  return trechos.map((t) => {
    if (t.t === 'negrito') return `<strong>${escapar(t.texto)}</strong>`;
    if (t.t === 'link') return `<a href="${escapar(t.href)}">${escapar(t.texto)}</a>`;
    return escapar(t.texto);
  }).join('');
}

// Os blocos em HTML, com o id de cada título de seção (## e ###) para o sumário.
export function blocosEmHtml(blocos: Bloco[]): string {
  return blocos.map((b) => {
    switch (b.tipo) {
      case 'h1': return `<h1>${escapar(b.texto)}</h1>`;
      case 'h2': case 'h3': return `<${b.tipo} id="${ancora(b.texto)}">${escapar(b.texto)}</${b.tipo}>`;
      case 'p': return `<p>${trechosEmHtml(b.trechos)}</p>`;
      case 'ul': return `<ul>\n${b.itens.map((i) => `<li>${trechosEmHtml(i)}</li>`).join('\n')}\n</ul>`;
      case 'tabela': return `<table>\n<thead><tr>${b.cabecalho.map((c) => `<th>${escapar(c)}</th>`).join('')}</tr></thead>\n<tbody>\n${
        b.linhas.map((l) => `<tr>${l.map((c) => `<td>${escapar(c)}</td>`).join('')}</tr>`).join('\n')}\n</tbody>\n</table>`;
    }
  }).join('\n');
}

export const markdownEmHtml = (md: string): string => blocosEmHtml(lerMarkdown(md));

// Os links do próprio site (/privacidade, /blog/...) com o endereço completo, para o Markdown que sai sozinho.
export const linksAbsolutos = (md: string, base: string): string => md.replace(/\]\(\//g, `](${base.replace(/\/+$/, '')}/`);
